import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, WorkflowStepInterface } from '@vendasta/automata';
import { Observable, of } from 'rxjs';
import { ActivityTableEntry, AutomationDisplayService, NodeData } from '../../common/automation-display.service';

export enum LIFECYCLE_STAGE {
  UNSET,
  LEAD,
  PROSPECT,
  CUSTOMER,
}

export interface LifecycleFormDataFormat {
  use_internal_evaluation: boolean;
  lifecycle_stage: number;
}

@Injectable({ providedIn: 'root' })
export class SetAccountLifecycleStageService extends AutomationDisplayService {
  constructor(protected readonly translateService: TranslateService) {
    super();
  }

  getActivityColumn(_namespace: string, activity: ActivityInterface): ActivityTableEntry {
    // The error case if the step isn't set up correctly
    if (activity?.error) {
      return this.tableEntry(
        this.translateService.stream(
          'AUTOMATIONS.EDITOR.TASKS.SET_ACCOUNT_LIFECYCLE_STAGE.ACTIVITY.FAILED_TO_SET_LIFECYCLE',
        ),
        this.emptyBody(),
      );
    }

    // Otherwise, we display what Lifecycle stage the account will be set to
    // Fall back to empty string if data is formatted incorrectly.
    const data: LifecycleFormDataFormat = activity?.workflowStep?.data ? JSON.parse(activity.workflowStep.data) : {};
    const body$ = this.generateBody(data);

    return this.tableEntry(
      this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.SET_ACCOUNT_LIFECYCLE_STAGE.ACTION.TITLE'),
      body$,
    );
  }

  getAutomationNodeData(
    _namespace: string,
    step: WorkflowStepInterface,
    _isWaitUntilNode: boolean,
  ): Observable<NodeData> {
    const data = step?.data ? JSON.parse(step.data) : {};
    const body$ = this.generateBody(data);

    if (body$) {
      return this.nodeDisplayData(body$, this.noValidationErrorsDetected());
    }

    return this.nodeDisplayData(
      this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.SET_ACCOUNT_LIFECYCLE_STAGE.ACTION.TITLE'),
      this.nonDismissibleValidationErrorsDetected(
        step,
        this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.SET_ACCOUNT_LIFECYCLE_STAGE.ACTION.CONFIG_ERROR'),
      ),
    );
  }

  private generateBody(data: LifecycleFormDataFormat): Observable<string> {
    let body$: Observable<string> = of('');
    if (data.use_internal_evaluation === true) {
      body$ = this.translateService.stream(
        'AUTOMATIONS.EDITOR.TASKS.SET_ACCOUNT_LIFECYCLE_STAGE.USE_INTERNAL_EVALUATION',
      );
      return body$;
    }

    let stageName = '';
    let validStage = true;
    switch (data.lifecycle_stage) {
      case LIFECYCLE_STAGE.LEAD:
        stageName = this.translateService.instant(
          'AUTOMATIONS.EDITOR.TASKS.SET_ACCOUNT_LIFECYCLE_STAGE.LIFECYCLE_STAGES.LEAD',
        );
        break;
      case LIFECYCLE_STAGE.PROSPECT:
        stageName = this.translateService.instant(
          'AUTOMATIONS.EDITOR.TASKS.SET_ACCOUNT_LIFECYCLE_STAGE.LIFECYCLE_STAGES.PROSPECT',
        );
        break;
      case LIFECYCLE_STAGE.CUSTOMER:
        stageName = this.translateService.instant(
          'AUTOMATIONS.EDITOR.TASKS.SET_ACCOUNT_LIFECYCLE_STAGE.LIFECYCLE_STAGES.CUSTOMER',
        );
        break;
      default:
        validStage = false;
    }

    if (validStage === true) {
      body$ = this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.SET_ACCOUNT_LIFECYCLE_STAGE.ACTION.SUBTITLE', {
        lifecycleStageName: stageName,
      });
    } else {
      body$ = this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.SET_ACCOUNT_LIFECYCLE_STAGE.ACTION.CONFIG_ERROR');
    }

    return body$;
  }
}
